// ============================================================================
// ADHKAR CORE UTILITIES
// ============================================================================

import type { 
  AdhkarItem, 
  AdhkarProgress, 
  AdhkarSettings, 
  AdhkarStats, 
  AdhkarResult,
  LanguageText 
} from './types';
import { 
  STORAGE_KEYS, 
  ERROR_CODES, 
  MESSAGES, 
  PERFORMANCE_CONSTANTS,
  DEFAULT_SETTINGS,
  DEFAULT_PROGRESS 
} from './constants';

/**
 * فئة إدارة التخزين المحلي
 */
export class LocalStorageManager {
  private static instance: LocalStorageManager;

  private constructor() {}

  static getInstance(): LocalStorageManager {
    if (!LocalStorageManager.instance) {
      LocalStorageManager.instance = new LocalStorageManager();
    }
    return LocalStorageManager.instance;
  }

  /**
   * حفظ البيانات في التخزين المحلي
   */
  setItem<T>(key: string, value: T): boolean {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * استرجاع البيانات من التخزين المحلي
   */
  getItem<T>(key: string, defaultValue?: T): T | null {
    try {
      if (typeof window !== 'undefined') {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue || null;
      }
      return defaultValue || null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue || null;
    }
  }

  /**
   * حذف عنصر من التخزين المحلي
   */
  removeItem(key: string): boolean {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }

  /**
   * مسح جميع البيانات
   */
  clear(): boolean {
    try {
      if (typeof window !== 'undefined') {
        localStorage.clear();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }
}

/**
 * فئة إدارة الإعدادات
 */
export class SettingsManager {
  private storage: LocalStorageManager;

  constructor() {
    this.storage = LocalStorageManager.getInstance();
  }

  /**
   * حفظ الإعدادات
   */
  saveSettings(settings: Partial<AdhkarSettings>): AdhkarResult<AdhkarSettings> {
    try {
      const currentSettings = this.getSettings();
      const newSettings = { ...currentSettings, ...settings };
      
      const success = this.storage.setItem(STORAGE_KEYS.ADHKAR_SETTINGS, newSettings);
      
      if (success) {
        return {
          success: true,
          data: newSettings,
          message: MESSAGES.SETTINGS_SAVED
        };
      } else {
        return {
          success: false,
          error: {
            code: ERROR_CODES.SETTINGS_SAVE_FAILED,
            message: MESSAGES.ERROR_OCCURRED,
            timestamp: new Date()
          }
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: ERROR_CODES.SETTINGS_SAVE_FAILED,
          message: MESSAGES.ERROR_OCCURRED,
          details: error,
          timestamp: new Date()
        }
      };
    }
  }

  /**
   * استرجاع الإعدادات
   */
  getSettings(): AdhkarSettings {
    return this.storage.getItem<AdhkarSettings>(
      STORAGE_KEYS.ADHKAR_SETTINGS, 
      DEFAULT_SETTINGS
    ) || DEFAULT_SETTINGS;
  }

  /**
   * إعادة تعيين الإعدادات
   */
  resetSettings(): AdhkarResult<AdhkarSettings> {
    return this.saveSettings(DEFAULT_SETTINGS);
  }
}

/**
 * فئة إدارة التقدم
 */
export class ProgressManager {
  private storage: LocalStorageManager;

  constructor() {
    this.storage = LocalStorageManager.getInstance();
  }

  /**
   * حفظ تقدم عنصر
   */
  saveProgress(itemId: string, progress: Partial<AdhkarProgress>): AdhkarResult<AdhkarProgress> {
    try {
      const allProgress = this.getAllProgress();
      const currentProgress = allProgress[itemId] || { ...DEFAULT_PROGRESS, itemId };
      
      const newProgress: AdhkarProgress = {
        ...currentProgress,
        ...progress,
        itemId
      };

      allProgress[itemId] = newProgress;
      
      const success = this.storage.setItem(STORAGE_KEYS.ADHKAR_PROGRESS, allProgress);
      
      if (success) {
        return {
          success: true,
          data: newProgress,
          message: MESSAGES.PROGRESS_SAVED
        };
      } else {
        return {
          success: false,
          error: {
            code: ERROR_CODES.PROGRESS_SAVE_FAILED,
            message: MESSAGES.ERROR_OCCURRED,
            timestamp: new Date()
          }
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: ERROR_CODES.PROGRESS_SAVE_FAILED,
          message: MESSAGES.ERROR_OCCURRED,
          details: error,
          timestamp: new Date()
        }
      };
    }
  }

  /**
   * استرجاع تقدم عنصر
   */
  getProgress(itemId: string): AdhkarProgress {
    const allProgress = this.getAllProgress();
    return allProgress[itemId] || { ...DEFAULT_PROGRESS, itemId };
  }

  /**
   * استرجاع جميع التقدم
   */
  getAllProgress(): Record<string, AdhkarProgress> {
    return this.storage.getItem<Record<string, AdhkarProgress>>(
      STORAGE_KEYS.ADHKAR_PROGRESS, 
      {}
    ) || {};
  }

  /**
   * حذف تقدم عنصر
   */
  deleteProgress(itemId: string): boolean {
    try {
      const allProgress = this.getAllProgress();
      delete allProgress[itemId];
      return this.storage.setItem(STORAGE_KEYS.ADHKAR_PROGRESS, allProgress);
    } catch (error) {
      console.error('Error deleting progress:', error);
      return false;
    }
  }

  /**
   * مسح جميع التقدم
   */
  clearAllProgress(): boolean {
    return this.storage.removeItem(STORAGE_KEYS.ADHKAR_PROGRESS);
  }
}

/**
 * فئة إدارة الإحصائيات
 */
export class StatsManager {
  private storage: LocalStorageManager;

  constructor() {
    this.storage = LocalStorageManager.getInstance();
  }

  /**
   * حساب الإحصائيات
   */
  calculateStats(): AdhkarStats {
    const progressManager = new ProgressManager();
    const allProgress = progressManager.getAllProgress();
    
    const completedItems = Object.values(allProgress).filter(p => p.isCompleted);
    const totalCompleted = completedItems.length;
    
    const currentStreak = this.calculateCurrentStreak(completedItems);
    const longestStreak = this.calculateLongestStreak(completedItems);
    
    const favoriteItems = this.getFavoriteItems();
    
    const lastCompleted = completedItems.length > 0 
      ? new Date(Math.max(...completedItems.map(p => p.completedAt?.getTime() || 0)))
      : undefined;

    const weeklyProgress = this.calculateWeeklyProgress(completedItems);
    const monthlyProgress = this.calculateMonthlyProgress(completedItems);

    return {
      totalCompleted,
      currentStreak,
      longestStreak,
      favoriteItems,
      lastCompleted,
      weeklyProgress,
      monthlyProgress
    };
  }

  /**
   * حفظ الإحصائيات
   */
  saveStats(stats: AdhkarStats): boolean {
    return this.storage.setItem(STORAGE_KEYS.ADHKAR_STATS, stats);
  }

  /**
   * استرجاع الإحصائيات
   */
  getStats(): AdhkarStats {
    return this.storage.getItem<AdhkarStats>(
      STORAGE_KEYS.ADHKAR_STATS, 
      this.calculateStats()
    ) || this.calculateStats();
  }

  /**
   * حساب التتابع الحالي
   */
  private calculateCurrentStreak(completedItems: AdhkarProgress[]): number {
    // تنفيذ منطق حساب التتابع الحالي
    return 0; // مؤقت
  }

  /**
   * حساب أطول تتابع
   */
  private calculateLongestStreak(completedItems: AdhkarProgress[]): number {
    // تنفيذ منطق حساب أطول تتابع
    return 0; // مؤقت
  }

  /**
   * الحصول على العناصر المفضلة
   */
  private getFavoriteItems(): string[] {
    return this.storage.getItem<string[]>(STORAGE_KEYS.ADHKAR_FAVORITES, []) || [];
  }

  /**
   * حساب التقدم الأسبوعي
   */
  private calculateWeeklyProgress(completedItems: AdhkarProgress[]): number {
    // تنفيذ منطق حساب التقدم الأسبوعي
    return 0; // مؤقت
  }

  /**
   * حساب التقدم الشهري
   */
  private calculateMonthlyProgress(completedItems: AdhkarProgress[]): number {
    // تنفيذ منطق حساب التقدم الشهري
    return 0; // مؤقت
  }
}

/**
 * فئة إدارة الأحداث
 */
export class EventManager {
  private listeners: Map<string, Function[]> = new Map();

  /**
   * إضافة مستمع حدث
   */
  addEventListener(eventType: string, callback: Function): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)!.push(callback);
  }

  /**
   * إزالة مستمع حدث
   */
  removeEventListener(eventType: string, callback: Function): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * إطلاق حدث
   */
  emit(eventType: string, data?: any): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event callback:', error);
        }
      });
    }
  }

  /**
   * مسح جميع المستمعين
   */
  clear(): void {
    this.listeners.clear();
  }
}

/**
 * فئة إدارة الأداء
 */
export class PerformanceManager {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();

  /**
   * حفظ في الذاكرة المؤقتة
   */
  setCache(key: string, data: any, duration: number = PERFORMANCE_CONSTANTS.CACHE_DURATION): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now() + duration
    });
  }

  /**
   * استرجاع من الذاكرة المؤقتة
   */
  getCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.timestamp) {
      return cached.data as T;
    }
    this.cache.delete(key);
    return null;
  }

  /**
   * مسح الذاكرة المؤقتة
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * تنظيف الذاكرة المؤقتة منتهية الصلاحية
   */
  cleanupCache(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now >= value.timestamp) {
        this.cache.delete(key);
      }
    }
  }
}

/**
 * أدوات مساعدة عامة
 */
export class Utils {
  /**
   * تأخير زمني
   */
  static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * توليد معرف فريد
   */
  static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * تنسيق التاريخ
   */
  static formatDate(date: Date, locale: string = 'ar-SA'): string {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * تنسيق الوقت
   */
  static formatTime(date: Date, locale: string = 'ar-SA'): string {
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * التحقق من صحة البيانات
   */
  static validateItem(item: any): item is AdhkarItem {
    return (
      item &&
      typeof item.id === 'string' &&
      typeof item.text === 'object' &&
      typeof item.text.ar === 'string' &&
      typeof item.text.en === 'string' &&
      typeof item.repeat === 'object' &&
      typeof item.repeat.ar === 'string' &&
      typeof item.repeat.en === 'string' &&
      typeof item.count === 'number' &&
      typeof item.type === 'string' &&
      ['quran', 'dua'].includes(item.type)
    );
  }

  /**
   * نسخ عميق للكائن
   */
  static deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * دمج الكائنات
   */
  static merge<T>(target: T, source: Partial<T>): T {
    return { ...target, ...source };
  }
}
